import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useShallow } from "zustand/react/shallow";

import { AllCommunityModule, type StateUpdatedEvent, type RowClickedEvent } from "ag-grid-community";
import { AgGridProvider } from "ag-grid-react";
const modules = [AllCommunityModule];

import { Button, FormControl, Input, MenuItem, Select, Tooltip } from "@mui/material";

import kutil, { type PageInfo } from "@modules/kutil";
import commonCode from "@modules/common_code";
import valueFormatter from "@modules/value_formatter";
import { useStableHandlers } from "@/modules/stable_handlers";

import { CustomAgGridReact, type CustomColDef, type CustomColGroupDef } from "@/components/aggrid/custom_ag_grid_react";

import router from "@user/router";
import { useTabsStore } from "@user/store/tabs_store";
import { useAuthStore } from "@user/store/auth_store";
import { useSettingsStore } from "@user/store/settings_store";

import "./location_status.scoped.scss";

interface State {
}

const defState = {
};

interface SearchParams {
  skuType: string,
  skuSize: string,
  disableCellYn: string,
  skuNoExLike: string,
  skuCellQty: string,
  locationIdFrom: string,
  locationFrom1: string,
  locationFrom2: string,
  locationFrom3: string,
  locationFrom4: string,
  locationIdTo: string,
  locationTo1: string,
  locationTo2: string,
  locationTo3: string,
  locationTo4: string,
  export: string,
  page: number,
  pageSize: number
}

const defSearchParams = {
  skuType: "",
  skuSize: "",
  disableCellYn: "",
  skuNoExLike: "",
  skuCellQty: "",
  locationIdFrom: "0100100101",
  locationFrom1: "1",
  locationFrom2: "1",
  locationFrom3: "1",
  locationFrom4: "1",
  locationIdTo: "0100104576",
  locationTo1: "1",
  locationTo2: "1",
  locationTo3: "45",
  locationTo4: "76",
  export: "",
  page: 1,
  pageSize: kutil.DEFAULT_PAGE_SIZE
};

interface Result {
  searchParams: SearchParams,
  pageInfo: PageInfo,
  infos: any[],
  detailInfos: any[]
}

const defResult = {
  searchParams: kutil.deepCopy(defSearchParams),
  pageInfo: {
    totalCount: 0,
    totalPage: 0,
    page: defSearchParams.page,
    pageSize: defSearchParams.pageSize
  },
  infos: [],
  detailInfos: []
};

function LocationStatus(props: any) {
  const [state, setState] = useState<State>(kutil.deepCopy(defState));
  const [searchParams, setSearchParams] = useState<SearchParams>(kutil.deepCopy(defSearchParams));
  const [result, setResult] = useState<Result>(kutil.deepCopy(defResult));

  const auth = useAuthStore(useShallow((state) => state.getAuth(props.id)));
  const location = useTabsStore((state) => state.getLocation(props.id));
  const gridState = useMemo(() => useSettingsStore.getState().getGridState(location.path), [location.path]);
  const gridState4detail = useMemo(() => useSettingsStore.getState().getGridState(location.path + ".detail"), [location.path]);

  const searchParamsRef = useRef(searchParams);
  const updateSearchParams = useCallback((patch: Partial<SearchParams>, isReset: boolean = false) => {
    if (isReset) {
      searchParamsRef.current = { ...patch } as SearchParams;
    } else {
      searchParamsRef.current = { ...searchParamsRef.current, ...patch };
    }
    setSearchParams(searchParamsRef.current);
    return searchParamsRef.current;
  }, []);

  const gridInfo = useMemo((): (CustomColDef | CustomColGroupDef)[] => [
    { headerName: "Location", field: "locationId", width: 140, cellStyle: {textAlign: "center", cursor: "pointer"}, valueFormatter: valueFormatter.locationId },
    { headerName: "SKU Type", field: "skuType", width: 120, cellStyle: {textAlign: "center", cursor: "pointer"}, valueFormatter: valueFormatter.commonCode("skuType") },
    { headerName: "SKU Size", field: "skuSize", width: 120, cellStyle: {textAlign: "center", cursor: "pointer"}, valueFormatter: valueFormatter.commonCode("skuSize") },
    { headerName: "금지Cell", field: "disableCellYn", width: 120, cellStyle: {textAlign: "center", cursor: "pointer"}, valueFormatter: valueFormatter.commonCode("yn") },
    { headerName: "SKU No", field: "skuNoEx", width: 120, cellStyle: {textAlign: "center", cursor: "pointer"} },
    { headerName: "SKU Cell", field: "skuCellQty", width: 120, cellStyle: { textAlign: "center"}, valueFormatter: valueFormatter.number },
    { headerName: "최근변경일", field: "recentUpdDate", width: 220, cellStyle: {textAlign: "center", cursor: "pointer"}, valueFormatter: valueFormatter.date("yyyy-MM-dd HH:mm:ss"), filter: true, filterParams: {buttons: ["reset"]} }
  ], []);

  ////////////////////////////////////////////////////////////////////////////////
  // grid 관련 기능들
  ////////////////////////////////////////////////////////////////////////////////
  const gridOptions = useMemo(() => ({
    rowSelection: {
      mode: "singleRow" as const,
      checkboxes: false,
      enableClickSelection: true
    },
  }), []);
  const gridCallbacks = useStableHandlers({
    onStateUpdated: (e: StateUpdatedEvent) => {
      kutil.onStateUpdated(e, location.path);
    },
    onRowClicked: (e: RowClickedEvent) => {
      showDetail(e.data);
    }
  });

  const gridInfo4detail = useMemo((): (CustomColDef | CustomColGroupDef)[] => [
    { headerName: "SKU No", field: "skuNoEx", width: 120, cellStyle: {textAlign: "center"} },
    { headerName: "SKU Cell", field: "skuCellQty", width: 120, cellStyle: { textAlign: "center"}, valueFormatter: valueFormatter.number },
    { headerName: "변경일", field: "regDate", width: 220, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.date("yyyy-MM-dd HH:mm:ss"), filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "사유", field: "locationUpdType", width: 120, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.commonCode("locationUpdType"), filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "작업자", field: "regName", width: 120, cellStyle: {textAlign: "center"}, filter: true, filterParams: {buttons: ["reset"]} }
  ], []);

  ////////////////////////////////////////////////////////////////////////////////
  // grid 관련 기능들
  ////////////////////////////////////////////////////////////////////////////////
  const gridCallbacks4detail = {
    onStateUpdated: (e: StateUpdatedEvent) => {
      kutil.onStateUpdated(e, location.path + ".detail");
    }
  };

  // location 변경시 처리
  useEffect(() => {
    init();
  }, [location.key]);

  const isInit = useRef(false);
  async function init() {
    try {
      if (isInit.current == false) {
        isInit.current = await initData();
      }
      if (isInit.current) {
        await initParams();
        await searchInfos();
      }
    } finally {
      kutil.setLoadingStatus(false);
    }
  }

  async function initData() {
    return true;
  }

  async function initParams() {
    const searchParams = kutil.deepCopy(defSearchParams);
    kutil.copyObj(Object.fromEntries(new URLSearchParams(location.search).entries()), searchParams);
    updateSearchParams(searchParams);
  }

  async function searchInfos() {
    const searchParams = searchParamsRef.current;

    try {
      const reqSearchParams = kutil.copyObj(searchParams, {} as SearchParams);
      const data = await kutil.sendGet("/api/report/location_status.data?mode=search", reqSearchParams, {});
      setResult({
        searchParams: reqSearchParams,
        pageInfo: data.pageInfo,
        infos: data.infos,
        detailInfos: [],
      });
    } catch(e) {
      console.log(e);
    }
  }
  
  async function doSearch(_e: any, reqParams: any|null = null) {
    const searchParams = updateSearchParams({ ...reqParams, page: 1 });
    router.navigateByPath("/report/location_status", searchParams);
  }

  const goPage = useCallback((page: number) => {
    const searchParams = updateSearchParams({ ...result.searchParams, page }, true);
    router.navigateByPath("/report/location_status", searchParams);
  }, [result, router, updateSearchParams]);

  async function resetParams() {
    router.navigateByPath("/report/location_status", {});
  }
  
  function changeLocation(e: any) {
    const name = e.target.name as string;
    const value = e.target.value as string;
    const isFrom = name.startsWith("locationFrom");
    const sort = Number(name.slice(-1));
    const TOTAL_LEVELS = 4;

    const next: any = { ...searchParams };
    const counter = isFrom ? `locationTo${sort}` : `locationFrom${sort}`;

    next[name] = value;

    if (value === "") {
      next[counter] = "";
      for (let i = sort + 1; i <= TOTAL_LEVELS; i++) {
        next[`locationFrom${i}`] = "";
        next[`locationTo${i}`] = "";
      }
    } else {
      if (next[counter] === "") {
        next[counter] = value;
      }
      for (let i = 1; i < sort; i++) {
        const fromKey = `locationFrom${i}`;
        const toKey = `locationTo${i}`;
        if (next[fromKey] === "") next[fromKey] = "1";
        if (next[toKey] === "") next[toKey] = "1";
      }
    }

    if (sort === 1 && value !== "") {
      const prefix = isFrom ? "locationFrom" : "locationTo";
      const isHigh = Number(value) >= 6;
      if (Number(next[`${prefix}3`]) > (isHigh ? 18 : 45)) next[`${prefix}3`] = "";
      if (Number(next[`${prefix}4`]) > (isHigh ? 15 : 76)) next[`${prefix}4`] = "";
    }

    const locationIdFrom = valueFormatter.locationIdParser(
      next.locationFrom1, next.locationFrom2, next.locationFrom3, next.locationFrom4, "0"
    );
    const locationIdTo = valueFormatter.locationIdParser(
      next.locationTo1, next.locationTo2, next.locationTo3, next.locationTo4, "9"
    );

    next.locationIdFrom = locationIdFrom;
    next.locationIdTo = locationIdTo;

    updateSearchParams(next);
    doSearch(e, next);
  }
 
  async function showDetail(info: any) {
    try {
      const data = await kutil.sendGet("/api/report/location_status.data?mode=searchDetail", {locationId: info.locationId}, {});
      setResult(prev => ({
        ...prev,
        detailInfos: data.infos,
      }));
    } catch(e) {
      console.log(e);
    }
  }

  async function downloadExcel() {
    const searchParams = kutil.copyObj(result.searchParams, {} as SearchParams);
    searchParams.export = "excel";
    searchParams.page = 1;
    searchParams.pageSize = 10000;
    try {
      await kutil.downloadFile("/api/report/location_status.download?mode=search", searchParams, {
        "fileName": "로케이션현황_" + kutil.date2string(kutil.getCurrLocalDate(), "yyyyMMddHHmmss") + ".xlsx"
      });
    } catch(e) {
      console.log(e);
    }
  }

  return (
    <>
      {/* contents */}
      <div className="contents">
        {/* contents 상단 */}
        <div className="contents-top">
          {/* 타이틀 영역 */}
          <div className="title-area">
            {/* 타이틀 */}
            <p>로케이션 현황</p>
          </div>
          {/*// 타이틀 영역 */}
          {/* 버튼 영역 */}
          <div className="top-btn-area">
            <Button variant="outlined" onClick={doSearch}>조회</Button>
            { auth.download &&
            <Button variant="outlined" onClick={downloadExcel}>엑셀다운</Button>
            }
          </div>
          {/*// 버튼 영역 */}
        </div>
        {/*// contents 상단 */}
        {/* 검색 */}
        <div className="srch-area">
          <div>
            <p>SKU Type</p>
            <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
              <Select
                value={searchParams.skuType}
                onChange={(e) => {doSearch(e, {skuType: e.target.value});}}
                displayEmpty
              >
                <MenuItem value="">전체</MenuItem>
                {(commonCode.skuType as any[]).map((code) => (
                  <MenuItem key={code.code} value={code.code}>{code.value}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div>
            <p>SKU Size</p>
            <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
              <Select
                value={searchParams.skuSize}
                onChange={(e) => {doSearch(e, {skuSize: e.target.value});}}
                displayEmpty
              >
                <MenuItem value="">전체</MenuItem>
                {(commonCode.skuSize as any[]).map((code) => (
                  <MenuItem key={code.code} value={code.code}>{code.value}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div>
            <p>금지 Cell</p>
            <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
              <Select
                value={searchParams.disableCellYn}
                onChange={(e) => {doSearch(e, {disableCellYn: e.target.value});}}
                displayEmpty
              >
                <MenuItem value="">전체</MenuItem>
                {(commonCode.yn as any[]).map((code) => (
                  <MenuItem key={code.code} value={code.code}>{code.value}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div>
            <p>SKU No</p>
            <Input
              inputProps={{ maxLength: 100 }}
              value={searchParams.skuNoExLike}
              onChange={(e) => {updateSearchParams({ skuNoExLike: e.target.value })}}
              onKeyDown={(e) => { kutil.onKeyDownForInput(e, doSearch) }}
              placeholder="SKU No"
            />
          </div>
          <div>
            <p>SKU Cell</p>
            <Input
              inputProps={{ maxLength: 100 }}
              value={searchParams.skuCellQty}
              onChange={(e) => {updateSearchParams({ skuCellQty: e.target.value })}}
              onKeyDown={(e) => { kutil.onKeyDownForInput(e, doSearch) }}
              placeholder="SKU Cell"
            />
          </div>
          <div className="select-location">
            <p>Location</p>
            <div>
              <FormControl>
                <Select
                  name="locationFrom1"
                  value={searchParams.locationFrom1}
                  onChange={changeLocation}
                  displayEmpty
                >
                  <MenuItem value="">SC</MenuItem>
                  {Array.from({ length: kutil.LOCATION_RANGE.level1 }, (_, i) => {
                    const v = String(i + 1);
                    return (
                      <MenuItem key={v} value={v}>
                        {v}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              <FormControl>
                <Select
                  name="locationFrom2"
                  value={searchParams.locationFrom2}
                  onChange={changeLocation}
                  displayEmpty
                >
                  <MenuItem value="">열</MenuItem>
                  {Array.from({ length: kutil.LOCATION_RANGE.level2 }, (_, i) => {
                    const v = String(i + 1);
                    return (
                      <MenuItem key={v} value={v}>
                        {v}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              <FormControl>
                <Select
                  name="locationFrom3"
                  value={searchParams.locationFrom3}
                  onChange={changeLocation}
                  displayEmpty
                >
                  <MenuItem value="">행</MenuItem>
                  {Array.from({ length: (Number(searchParams.locationFrom1) >= kutil.LOCATION_RANGE.startWp ? kutil.LOCATION_RANGE.level3.wp : kutil.LOCATION_RANGE.level3.wb) }, (_, i) => {
                    const v = String(i + 1);
                    return (
                      <MenuItem key={v} value={v}>
                        {v}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              <FormControl>
                <Select
                  name="locationFrom4"
                  value={searchParams.locationFrom4}
                  onChange={changeLocation}
                  displayEmpty
                >
                  <MenuItem value="">단</MenuItem>
                  {Array.from({ length: (Number(searchParams.locationFrom1) >= kutil.LOCATION_RANGE.startWp ? kutil.LOCATION_RANGE.level4.wp : kutil.LOCATION_RANGE.level4.wb) }, (_, i) => {
                    const v = String(i + 1);
                    return (
                      <MenuItem key={v} value={v}>
                        {v}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              <span>-</span>
              <FormControl>
                <Select
                  name="locationTo1"
                  value={searchParams.locationTo1}
                  onChange={changeLocation}
                  displayEmpty
                >
                  <MenuItem value="">SC</MenuItem>
                  {Array.from({ length: kutil.LOCATION_RANGE.level1 }, (_, i) => {
                    const v = String(i + 1);
                    return (
                      <MenuItem key={v} value={v}>
                        {v}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              <FormControl>
                <Select
                  name="locationTo2"
                  value={searchParams.locationTo2}
                  onChange={changeLocation}
                  displayEmpty
                >
                  <MenuItem value="">열</MenuItem>
                  {Array.from({ length: kutil.LOCATION_RANGE.level2 }, (_, i) => {
                    const v = String(i + 1);
                    return (
                      <MenuItem key={v} value={v}>
                        {v}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              <FormControl>
                <Select
                  name="locationTo3"
                  value={searchParams.locationTo3}
                  onChange={changeLocation}
                  displayEmpty
                >
                  <MenuItem value="">행</MenuItem>
                  {Array.from({ length: (Number(searchParams.locationTo1) >= kutil.LOCATION_RANGE.startWp ? kutil.LOCATION_RANGE.level3.wp : kutil.LOCATION_RANGE.level3.wb) }, (_, i) => {
                    const v = String(i + 1);
                    return (
                      <MenuItem key={v} value={v}>
                        {v}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              <FormControl>
                <Select
                  name="locationTo4"
                  value={searchParams.locationTo4}
                  onChange={changeLocation}
                  displayEmpty
                >
                  <MenuItem value="">단</MenuItem>
                  {Array.from({ length: (Number(searchParams.locationTo1) >= kutil.LOCATION_RANGE.startWp ? kutil.LOCATION_RANGE.level4.wp : kutil.LOCATION_RANGE.level4.wb) }, (_, i) => {
                    const v = String(i + 1);
                    return (
                      <MenuItem key={v} value={v}>
                        {v}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </div>
          </div>
          <p>
            <Tooltip title="초기화">
              <button className="icon-button form-reset" onClick={resetParams}>초기화</button>
            </Tooltip>
          </p>
        </div>
        {/*// 검색 */}
        {/* 로케이션 정보 */}
        <AgGridProvider modules={modules}>
          <div className="grid-area">
            <CustomAgGridReact
              columnDefs={gridInfo}
              rowData={result.infos}
              defaultColDef={kutil.defaultColDef}
              initialState={gridState}
              rowSelection={gridOptions.rowSelection}
              onStateUpdated={gridCallbacks.onStateUpdated}
              onRowClicked={gridCallbacks.onRowClicked}
              totalPage={result.pageInfo.totalPage}
              currPage={result.pageInfo.page}
              onPageChange={goPage}
            />
          </div>
        </AgGridProvider>
        {/*// 로케이션 정보 */}
        {/* 서브 타이틀 */}
        <div className="sub-title-area">
          <p>이력정보</p>
        </div>
        {/* 로케이션 이력 정보 */}
        <AgGridProvider modules={modules}>
          <div className="grid-area">
            <CustomAgGridReact
              columnDefs={gridInfo4detail}
              rowData={result.detailInfos}
              defaultColDef={kutil.defaultColDef}
              initialState={gridState4detail}
              showInfo={false}
              onStateUpdated={gridCallbacks4detail.onStateUpdated}
            />
          </div>
        </AgGridProvider>
        {/*// 로케이션 이력 정보 */}
      </div>
      {/*// contents */}
    </>
  );
}

export default React.memo(LocationStatus);
